import Cookies from 'js-cookie';

/**
 * Writes a cookie only once the visitor has consented to its category.
 *
 * The site's own functional cookies — directory and careers filters, dismissed
 * alerts, the signup-to-preferences handoff — are set by the main JavaScript
 * bundle. CookieYes blocks cookies by blocking the script that sets them, so it
 * cannot gate these: the only script it could block is /built/app-*.js, and
 * blocking that would take the whole site with it. CookieYes flags them as
 * "no script URL pattern" for exactly this reason.
 *
 * So the gate lives here instead. Reads are untouched — Cookies.get() is used
 * directly wherever a value is read back. Only writes go through this.
 *
 * When consent is absent or refused, nothing is written and the feature
 * degrades quietly: filters still work for the current page, they just do not
 * survive a navigation. A consent granted later does not replay writes that
 * were skipped; the next interaction stores them normally.
 */

/**
 * CookieYes records the visitor's choices in a cookie of its own, as a flat
 * comma-separated string:
 *
 *   consentid:xxx,consent:yes,action:yes,necessary:yes,functional:yes,
 *   analytics:no,performance:no,advertisement:no
 *
 * Reading that cookie directly, rather than a global CookieYes object, keeps
 * this working regardless of when the CookieYes script finishes loading — the
 * cookie is present from the first request after a choice is made.
 *
 * VERIFY BOTH OF THESE against the CookieYes account before trusting the gate:
 * the cookie name varies by plan, and the category keys must match the
 * categories actually enabled there. Everything else in this file is
 * independent of that format.
 */
const CONSENT_COOKIE = 'cookieyes-consent';

/**
 * Whether the visitor has accepted a given CookieYes category.
 *
 * Returns false when no choice has been recorded yet, so cookies are withheld
 * until the banner is answered rather than written on the way past it.
 *
 * @param {string} category CookieYes category key, e.g. 'functional'
 * @return {boolean}
 */
export function hasConsent(category) {
    const raw = Cookies.get(CONSENT_COOKIE);

    if (!raw) {
        return false;
    }

    // Anchored on a separator so 'analytics' cannot match inside another key.
    return new RegExp('(^|,)\\s*' + category + '\\s*:\\s*yes\\s*(,|$)').test(raw);
}

const ConsentedCookie = {
    /**
     * Sets a cookie if its category has been consented to.
     *
     * Same signature as Cookies.set(), with the category inserted before the
     * options so call sites read as a sentence.
     *
     * @param {string} name
     * @param {string} value
     * @param {string} category CookieYes category key; defaults to 'functional'
     * @param {object} [options] passed through to js-cookie
     * @return {boolean} whether the cookie was written
     */
    set(name, value, category = 'functional', options = undefined) {
        if (!hasConsent(category)) {
            return false;
        }

        Cookies.set(name, value, options);

        return true;
    },
};

export default ConsentedCookie;
